// ============================================================
// Fee Structure Components & Installments Page
// ============================================================

import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Button, IconButton, Chip, TextField, MenuItem, FormControl, InputLabel, Select, FormControlLabel, Switch, Paper, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, InputAdornment } from '@mui/material';
import { Plus, Edit2, Trash2, AlertCircle, Search, CheckCircle2, X } from 'lucide-react';
import { DataGrid } from '@mui/x-data-grid';
import { PageHeader } from '@/components/navigation/PageHeader';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useSchool } from '@/hooks/useSchool';
import { feeService } from '@/services/feeService';
import { useFeeTypes } from '@/hooks/useFeeTypes';
import { academicSessionsApi } from '@/api/academicSessions.api';
import { studentsApi } from '@/api/students.api';
import { useFeeStructureItems, useCreateFeeStructureItem, useUpdateFeeStructureItem, useDeleteFeeStructureItem } from '@/hooks/useFeeStructureItems';
import { useFeeInstallments } from '@/hooks/useFeeInstallments';
import { PATHS } from '@/router/paths';
import { tokens } from '@/theme/tokens';
import toast from 'react-hot-toast';
import { FeeSetupGuideBanner } from '@/components/fees/FeeSetupGuideBanner';

const FREQUENCIES = [
  { value: 'ONE_TIME', label: 'One Time' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'HALF_YEARLY', label: 'Half Yearly' },
  { value: 'YEARLY', label: 'Yearly' },
  { value: 'CUSTOM', label: 'Custom' },
];

const CATEGORY_MAP = {
  ACADEMIC: { label: 'Academic', color: 'primary' },
  TRANSPORT: { label: 'Transport', color: 'warning' },
  HOSTEL: { label: 'Hostel', color: 'secondary' },
  EXAM: { label: 'Exam', color: 'error' },
  LIBRARY: { label: 'Library', color: 'info' },
  SPORTS: { label: 'Sports', color: 'success' },
  OTHER: { label: 'Other', color: 'default' },
};

const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: 300,
    },
  },
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },
};

function InstallmentStatusBadge({ itemId, onClick }) {
  const { data: installments = [], isLoading } = useFeeInstallments(itemId);

  if (isLoading) {
    return <Chip label="Checking..." size="small" variant="outlined" sx={{ fontWeight: 600, fontSize: '0.7rem' }} />;
  }

  const isConfigured = installments && installments.length > 0;

  return (
    <Tooltip title={isConfigured ? `${installments.length} installment(s) configured. Click to view/edit.` : 'Installments not configured yet. Click to configure.'}>
      <Chip
        icon={isConfigured ? <CheckCircle2 size={13} /> : <AlertCircle size={13} />}
        label={isConfigured ? `${installments.length} Configured` : 'Not Configured'}
        size="small"
        color={isConfigured ? 'success' : 'warning'}
        variant={isConfigured ? 'filled' : 'outlined'}
        onClick={onClick}
        sx={{
          fontWeight: 700,
          fontSize: '0.72rem',
          cursor: 'pointer',
          '&:hover': {
            opacity: 0.85,
          },
        }}
      />
    </Tooltip>
  );
}

export function FeeStructureComponentsPage() {
  const { id: feeStructureId } = useParams();
  const navigate = useNavigate();
  const { schoolId } = useSchool();

  usePageMeta('Fee Components', [
    { label: 'Fees', path: PATHS.FEES },
    { label: 'Structure', path: PATHS.FEES_STRUCTURE },
    { label: 'Components' },
  ]);

  // Reference & Metadata states
  const [feeStructure, setFeeStructure] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [isLoadingStructure, setIsLoadingStructure] = useState(true);

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOptional, setFilterOptional] = useState('ALL');

  // Master fee types list
  const { data: feeTypes = [] } = useFeeTypes();

  // Fee items attached to structure
  const { data: items = [], isLoading: itemsLoading } = useFeeStructureItems(feeStructureId);
  const { mutate: createItem, isPending: isCreatingItem } = useCreateFeeStructureItem();
  const { mutate: updateItem, isPending: isUpdatingItem } = useUpdateFeeStructureItem();
  const { mutate: deleteItem, isPending: isDeletingItem } = useDeleteFeeStructureItem();

  // State for Add/Edit Item modal
  const [openItemForm, setOpenItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemFormData, setItemFormData] = useState({
    fee_type_id: '',
    amount: '',
    installment_type: 'ONE_TIME',
    sort_order: 0,
    is_optional: false,
    is_active: true,
  });

  // Delete dialog state
  const [deleteItemRow, setDeleteItemRow] = useState(null);

  // Load Structure, Sessions, and Classes info
  useEffect(() => {
    if (schoolId && feeStructureId) {
      setIsLoadingStructure(true);

      // Fetch Fee Structure
      feeService
        .getFeeStructureById(schoolId, feeStructureId)
        .then((res) => setFeeStructure(res?.data || res))
        .catch(console.error)
        .finally(() => setIsLoadingStructure(false));

      // Fetch Sessions for lookup
      academicSessionsApi
        .getAllBySchool(schoolId)
        .then((res) => setSessions(Array.isArray(res) ? res : res?.data || []))
        .catch(console.error);

      // Fetch Classes for lookup
      studentsApi
        .getClassesSections()
        .then((res) => setClasses(res?.data || (Array.isArray(res) ? res : [])))
        .catch(console.error);
    }
  }, [schoolId, feeStructureId]);

  // Name Resolvers
  const sessionName = useMemo(() => {
    if (!feeStructure?.academic_year_id || sessions.length === 0) return '';
    const s = sessions.find((s) => s.id === feeStructure.academic_year_id);
    return s ? s.session_name || s.name || `${s.start_date} - ${s.end_date}` : '';
  }, [feeStructure, sessions]);

  const className = useMemo(() => {
    if (!feeStructure?.class_id || classes.length === 0) return '';
    const c = classes.find((c) => (c.id || c.class_id) === feeStructure.class_id);
    return c ? c.class_name || c.name : '';
  }, [feeStructure, classes]);

  const sectionName = useMemo(() => {
    if (!feeStructure?.class_id || !feeStructure?.section_id || classes.length === 0) return '';
    const c = classes.find((c) => (c.id || c.class_id) === feeStructure.class_id);
    const sec = (c?.sections || []).find((s) => (s.id || s.section_id) === feeStructure.section_id);
    return sec ? sec.section_name || sec.name : '';
  }, [feeStructure, classes]);

  // Total Structure Fee calculation
  const totalStructureFee = useMemo(() => {
    return items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  }, [items]);

  // Filtered Fee Items
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const feeTypeInfo = item.fee_type || feeTypes.find((ft) => ft.id === item.fee_type_id);
      const name = feeTypeInfo?.name || '';
      const code = feeTypeInfo?.code || '';

      const matchesSearch =
        !searchTerm ||
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        code.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesOptional =
        filterOptional === 'ALL' ||
        (filterOptional === 'OPTIONAL' && item.is_optional) ||
        (filterOptional === 'MANDATORY' && !item.is_optional);

      return matchesSearch && matchesOptional;
    });
  }, [items, feeTypes, searchTerm, filterOptional]);

  // Filter out fee types that are already added to this fee structure (unless editing or currently selected)
  // and exclude inactive fee types for new component additions.
  const availableFeeTypes = useMemo(() => {
    const existingFeeTypeIds = new Set(items.map((item) => String(item.fee_type_id || item.fee_type?.id || '')));
    const filtered = feeTypes.filter((ft) => {
      const ftId = String(ft.id);
      const isEditingThisType = editingItem && (String(editingItem.fee_type_id) === ftId || String(editingItem.fee_type?.id) === ftId);
      const isCurrentlySelected = itemFormData.fee_type_id && String(itemFormData.fee_type_id) === ftId;

      // Do not show inactive fee types unless editing/selecting an item already assigned to this type
      if (!ft.is_active && !isEditingThisType && !isCurrentlySelected) {
        return false;
      }

      if (isEditingThisType || isCurrentlySelected) {
        return true;
      }
      return !existingFeeTypeIds.has(ftId);
    });

    if (editingItem && editingItem.fee_type && !filtered.some((ft) => String(ft.id) === String(editingItem.fee_type.id || editingItem.fee_type_id))) {
      filtered.push(editingItem.fee_type);
    }

    return filtered;
  }, [feeTypes, items, editingItem, itemFormData.fee_type_id]);

  // Handlers for Add/Edit Fee Component
  const handleOpenAddItem = () => {
    setEditingItem(null);
    const existingFeeTypeIds = new Set(items.map((item) => item.fee_type_id || item.fee_type?.id));
    const activeFeeTypes = feeTypes.filter((ft) => ft.is_active !== false);
    const unaddedTypes = activeFeeTypes.filter((ft) => !existingFeeTypeIds.has(ft.id));

    if (unaddedTypes.length === 0) {
      if (activeFeeTypes.length === 0) {
        toast.error('No active fee types found. Please activate or create fee types first.');
      } else {
        toast.error('All available active fee types have already been added to this fee structure.');
      }
      return;
    }

    const initialType = unaddedTypes[0];
    let initialFreq = initialType ? (initialType.frequency || 'ONE_TIME') : 'ONE_TIME';
    if (initialFreq === 'ANNUALLY') initialFreq = 'YEARLY';
    if (initialFreq === 'PER_TERM') initialFreq = 'CUSTOM';

    setItemFormData({
      fee_type_id: initialType ? initialType.id : '',
      amount: '',
      installment_type: initialFreq,
      sort_order: items.length + 1,
      is_optional: initialType ? !!initialType.is_optional : false,
      is_active: true,
    });
    setOpenItemForm(true);
  };

  const handleOpenEditItem = (item) => {
    let instType = item.installment_type || 'ONE_TIME';
    if (instType === 'ANNUALLY') instType = 'YEARLY';
    if (instType === 'PER_TERM') instType = 'CUSTOM';
    const matchedFt = feeTypes.find((ft) => ft.id === (item.fee_type_id || item.fee_type?.id));
    setEditingItem(item);
    setItemFormData({
      fee_type_id: item.fee_type_id || item.fee_type?.id || '',
      amount: item.amount || '',
      installment_type: instType,
      sort_order: item.sort_order ?? 0,
      is_optional: matchedFt ? !!matchedFt.is_optional : !!item.is_optional,
      is_active: item.is_active ?? true,
    });
    setOpenItemForm(true);
  };

  const handleItemSubmit = (e) => {
    e.preventDefault();
    if (!itemFormData.fee_type_id) {
      toast.error('Fee Type is required');
      return;
    }
    if (!itemFormData.amount || Number(itemFormData.amount) <= 0) {
      toast.error('Valid Amount is required');
      return;
    }

    const selectedFeeType = feeTypes.find((ft) => ft.id === itemFormData.fee_type_id);
    let instType = selectedFeeType?.frequency || itemFormData.installment_type || 'ONE_TIME';
    if (instType === 'ANNUALLY') instType = 'YEARLY';
    if (instType === 'PER_TERM') instType = 'CUSTOM';

    const payload = {
      fee_type_id: itemFormData.fee_type_id,
      amount: Number(itemFormData.amount),
      installment_type: instType,
      sort_order: editingItem ? (Number(itemFormData.sort_order) || 1) : (items.length + 1),
      is_optional: itemFormData.is_optional,
      is_active: itemFormData.is_active,
    };

    if (editingItem) {
      updateItem(
        { itemId: editingItem.id, feeStructureId, payload },
        {
          onSuccess: () => {
            toast.success('Fee Component updated successfully!');
            setOpenItemForm(false);
          },
          onError: (err) => {
            const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to update component';
            toast.error(errorMsg);
          },
        }
      );
    } else {
      createItem(
        { feeStructureId, payload },
        {
          onSuccess: () => {
            toast.success('Fee Component added successfully!');
            setOpenItemForm(false);
          },
          onError: (err) => {
            const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to add component';
            toast.error(errorMsg);
          },
        }
      );
    }
  };

  const handleDeleteConfirm = () => {
    if (!deleteItemRow) return;
    deleteItem(
      { itemId: deleteItemRow.id, feeStructureId },
      {
        onSuccess: () => {
          toast.success('Fee component removed successfully!');
          setDeleteItemRow(null);
        },
        onError: (err) => {
          const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Failed to remove component';
          toast.error(errorMsg);
        },
      }
    );
  };

  const columns = useMemo(
    () => [
      {
        field: 'code',
        headerName: 'Code',
        flex: 1.1,
        minWidth: 150,
        renderCell: (params) => {
          const feeTypeInfo = params.row.fee_type || feeTypes.find((ft) => ft.id === params.row.fee_type_id);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              {feeTypeInfo?.code ? (
                <Chip
                  label={feeTypeInfo.code}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    letterSpacing: '0.5px',
                    borderColor: tokens.colors.primary.main,
                    color: tokens.colors.primary.main,
                    backgroundColor: `${tokens.colors.primary.main}0D`,
                    maxWidth: 'none',
                  }}
                />
              ) : (
                '—'
              )}
            </Box>
          );
        },
      },
      {
        field: 'name',
        headerName: 'Fee Type Name',
        flex: 1.5,
        minWidth: 160,
        renderCell: (params) => {
          const feeTypeInfo = params.row.fee_type || feeTypes.find((ft) => ft.id === params.row.fee_type_id);
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2" fontWeight={600} color="text.primary">
                {feeTypeInfo?.name || 'Unknown Fee Type'}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'category',
        headerName: 'Category',
        flex: 0.9,
        minWidth: 110,
        renderCell: (params) => {
          const feeTypeInfo = params.row.fee_type || feeTypes.find((ft) => ft.id === params.row.fee_type_id);
          if (!feeTypeInfo?.category) return '—';
          const cat = CATEGORY_MAP[feeTypeInfo.category] || { label: feeTypeInfo.category, color: 'default' };
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Chip label={cat.label} size="small" color={cat.color} />
            </Box>
          );
        },
      },
      {
        field: 'amount',
        headerName: 'Amount',
        flex: 0.9,
        minWidth: 110,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%', width: '100%' }}>
            <Typography variant="body2" fontWeight={700} color="primary.main">
              ₹{(Number(params.value) || 0).toLocaleString()}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'installment_type',
        headerName: 'Frequency',
        flex: 0.85,
        minWidth: 100,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {FREQUENCIES.find((f) => f.value === params.value)?.label || params.value?.replace('_', ' ') || 'One Time'}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'installments_status',
        headerName: 'Installments',
        flex: 1.1,
        minWidth: 140,
        align: 'center',
        headerAlign: 'center',
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <InstallmentStatusBadge
              itemId={params.row.id}
              onClick={() => navigate(`/fees/structure/${feeStructureId}/components/${params.row.id}/installments`)}
            />
          </Box>
        ),
      },
      {
        field: 'is_optional',
        headerName: 'Optional',
        flex: 0.8,
        minWidth: 95,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Chip
              label={params.value ? 'Optional' : 'Mandatory'}
              size="small"
              color={params.value ? 'warning' : 'success'}
              variant="outlined"
            />
          </Box>
        ),
      },
      {
        field: 'is_active',
        headerName: 'Status',
        flex: 0.7,
        minWidth: 85,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Chip
              label={params.value !== false ? 'Active' : 'Inactive'}
              size="small"
              color={params.value !== false ? 'success' : 'error'}
              sx={{ fontWeight: 500 }}
            />
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 85,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Tooltip title="Edit Component">
              <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); handleOpenEditItem(params.row); }}>
                <Edit2 size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Remove Component">
              <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setDeleteItemRow(params.row); }}>
                <Trash2 size={16} />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [feeTypes, feeStructureId, navigate]
  );

  return (
    <Box sx={{ pb: 6, width: '100%' }}>
      <PageHeader
        title="Fee Components & Installments"
        subtitle="Manage attached fee components and installment schedules for this structure."
        onBack={() => navigate(PATHS.FEES_STRUCTURE)}
      />

      {/* Interactive Workflow Guide Banner */}
      <FeeSetupGuideBanner currentStep={2} structureId={feeStructureId} />

      {/* Plan Header Info Widget */}
      <WidgetContainer sx={{ mt: 3, p: 3, width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={700} color="primary.main">
              {feeStructure?.name || 'Fee Structure Plan'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {feeStructure?.description || 'Fee structure plan for managing student fee components.'}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
              {isLoadingStructure && (
                <Chip label="Loading details..." size="small" variant="outlined" />
              )}
              {sessionName && (
                <Chip label={`Session: ${sessionName}`} size="small" variant="outlined" color="primary" />
              )}
              {className && (
                <Chip label={`Class: ${className}`} size="small" variant="outlined" color="info" />
              )}
              {sectionName && (
                <Chip label={`Section: ${sectionName}`} size="small" variant="outlined" color="secondary" />
              )}
            </Box>
          </Box>

          <Paper
            elevation={0}
            sx={{
              py: 1,
              px: 2,
              borderRadius: 1.5,
              bgcolor: `${tokens.colors.primary.main}0E`,
              border: `1px solid ${tokens.colors.primary.main}30`,
              textAlign: 'right',
              alignSelf: 'center',
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.6875rem' }}>
              Total Structure Fee
            </Typography>
            <Typography variant="h6" fontWeight={800} color="primary.main" sx={{ lineHeight: 1.2, mt: 0.2 }}>
              ₹{totalStructureFee.toLocaleString()}
            </Typography>
          </Paper>
        </Box>
      </WidgetContainer>

      {/* Main Fee Components Table Widget (Standard Edge-to-Edge ERP Table) */}
      <WidgetContainer noPadding sx={{ mt: 3 }}>
        {/* Table Search & Toolbar */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${tokens.colors.border}`, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 2, flex: 1, flexWrap: 'wrap', minWidth: { xs: '100%', sm: 300 } }}>
            <TextField
              placeholder="Search by component name or code..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 1, minWidth: { xs: '100%', sm: 260 } }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} color={tokens.colors.text.secondary} />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Type Filter</InputLabel>
              <Select
                value={filterOptional}
                label="Type Filter"
                onChange={(e) => setFilterOptional(e.target.value)}
                MenuProps={SELECT_MENU_PROPS}
              >
                <MenuItem value="ALL">All Types</MenuItem>
                <MenuItem value="MANDATORY">Mandatory</MenuItem>
                <MenuItem value="OPTIONAL">Optional</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={handleOpenAddItem}
          >
            Add Component
          </Button>
        </Box>

        {itemsLoading ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Loading fee components...
          </Typography>
        ) : filteredItems.length === 0 ? (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <AlertCircle size={36} color={tokens.colors.text.secondary} style={{ marginBottom: 12 }} />
            <Typography variant="subtitle1" fontWeight={600} color="text.primary" gutterBottom>
              No Fee Components Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 450, mx: 'auto', mb: 3 }}>
              {searchTerm || filterOptional !== 'ALL'
                ? 'No fee components match your current filters.'
                : 'Click "Add Component" above to attach fee types (e.g. Tuition Fee, Admission Fee) and define amounts & payment schedules.'}
            </Typography>
            {(!searchTerm && filterOptional === 'ALL') && (
              <Button
                variant="contained"
                startIcon={<Plus size={16} />}
                onClick={handleOpenAddItem}
              >
                Add Component
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ width: '100%' }}>
            <DataGrid
              autoHeight
              rows={filteredItems}
              columns={columns}
              loading={itemsLoading}
              pageSizeOptions={[10, 25, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
              }}
              disableRowSelectionOnClick
              onRowClick={(params) => navigate(`/fees/structure/${feeStructureId}/components/${params.row.id}/installments`)}
              getRowId={(row) => row.id}
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                '& .MuiDataGrid-row': { cursor: 'pointer' },
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            />
          </Box>
        )}
      </WidgetContainer>

      {/* Add / Edit Fee Component Dialog */}
      <Dialog open={openItemForm} onClose={() => setOpenItemForm(false)} maxWidth="xs" fullWidth disableRestoreFocus>
        <form onSubmit={handleItemSubmit}>
          <DialogTitle sx={{ fontWeight: 600 }}>
            {editingItem ? 'Edit Fee Component' : 'Add Fee Component'}
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
              <FormControl size="small" fullWidth required>
                <InputLabel>Fee Type</InputLabel>
                <Select
                  value={itemFormData.fee_type_id}
                  label="Fee Type"
                  onChange={(e) => {
                    const selectedFt = feeTypes.find((ft) => ft.id === e.target.value);
                    let freq = selectedFt?.frequency || 'ONE_TIME';
                    if (freq === 'ANNUALLY') freq = 'YEARLY';
                    if (freq === 'PER_TERM') freq = 'CUSTOM';
                    setItemFormData({
                      ...itemFormData,
                      fee_type_id: e.target.value,
                      installment_type: freq,
                      is_optional: selectedFt ? !!selectedFt.is_optional : false,
                    });
                  }}
                  MenuProps={SELECT_MENU_PROPS}
                >
                  {availableFeeTypes.map((ft) => {
                    const freqLabel = FREQUENCIES.find((f) => f.value === ft.frequency)?.label || ft.frequency || 'One Time';
                    return (
                      <MenuItem key={ft.id} value={ft.id}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', gap: 1 }}>
                          <span>{ft.name} ({ft.code})</span>
                          <Chip label={freqLabel} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <TextField
                label="Amount (₹)"
                type="number"
                required
                fullWidth
                size="small"
                placeholder="e.g. 10000"
                value={itemFormData.amount}
                onChange={(e) => setItemFormData({ ...itemFormData, amount: e.target.value })}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={itemFormData.is_optional}
                    disabled
                  />
                }
                label="Optional for Students"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={itemFormData.is_active}
                    onChange={(e) => setItemFormData({ ...itemFormData, is_active: e.target.checked })}
                    color="success"
                  />
                }
                label="Is Active"
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpenItemForm(false)} disabled={isCreatingItem || isUpdatingItem}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={isCreatingItem || isUpdatingItem}>
              {isCreatingItem || isUpdatingItem ? 'Saving...' : editingItem ? 'Save Changes' : 'Add Component'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Compact Delete Component Confirmation Dialog */}
      <Dialog
        open={!!deleteItemRow}
        onClose={() => setDeleteItemRow(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            p: 0.5,
            boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5, pt: 1.5, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.primary">
            Remove Fee Component
          </Typography>
          <IconButton size="small" onClick={() => setDeleteItemRow(null)}>
            <X size={16} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 1, px: 2 }}>
          {deleteItemRow && (
            <Typography variant="body2" color="text.secondary">
              Are you sure you want to remove <strong>{deleteItemRow.fee_type?.name || feeTypes.find((ft) => ft.id === deleteItemRow.fee_type_id)?.name || 'this fee component'}</strong> from the structure?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => setDeleteItemRow(null)}
            disabled={isDeletingItem}
            sx={{ borderRadius: 1.5, px: 2, py: 0.5, textTransform: 'none', fontSize: '0.8125rem' }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={isDeletingItem}
            sx={{ borderRadius: 1.5, fontWeight: 600, px: 2.5, py: 0.5, textTransform: 'none', fontSize: '0.8125rem' }}
          >
            {isDeletingItem ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
